import { FC, ReactNode } from "react";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { DataType } from "src/constants";
import { formatTimeFromUnit, getValue, handlers } from "src/utility";
import { compactNumber } from "src/utility";
import { SorterLabel } from "src/components/SortersBar/SorterButton";

export function computeFlightLabels(group: Map<string | number, any[]>, sorter: any) {
  const keys = Array.from(group.keys());
  const handler = handlers[sorter.category as DataType];
  const parsed = keys.map((k) => handler(k as any)).filter((v) => v != null);

  let min: any = null;
  let max: any = null;

  if (sorter.category === "number") {
    const nums = parsed.filter((n) => !Number.isNaN(n)) as number[];
    if (nums.length) {
      min = Math.min(...nums);
      max = Math.max(...nums);
    }
  }

  if (sorter.category === "date") {
    const dates = parsed as Dayjs[];
    if (dates.length) {
      min = dates.reduce((a, b) => (b.isBefore(a) ? b : a));
      max = dates.reduce((a, b) => (b.isAfter(a) ? b : a));
    }
  }

  const minTicket = group.get(min)?.[0];
  const maxTicket = group.get(max)?.[0];

  return {
    minLabel: minTicket
      ? {
          price: compactNumber(getValue(minTicket, ["price", "total"])),
          duration: formatTimeFromUnit(getValue(minTicket, ["segments", 0, "durationMinutes"]), "m"),
          stops: getValue(minTicket, ["segments", 0, "stops"]),
        }
      : null,
    maxLabel: maxTicket
      ? {
          price: compactNumber(getValue(maxTicket, ["price", "total"])),
          duration: formatTimeFromUnit(getValue(maxTicket, ["segments", 0, "durationMinutes"]), "m"),
          stops: getValue(maxTicket, ["segments", 0, "stops"]),
        }
      : null,
  };
}

export const DefaultLabel: FC<{ label: SorterLabel | null }> = ({ label }) => {
  const { t } = useTranslation();
  if (!label) return null;
  const stopsLabel =
    label.stops === 0
      ? t("Flights.listing.direct")
      : label.stops === 1
        ? t("Flights.listing.oneStop")
        : t("Flights.listing.multipleStops", { count: label.stops });
  return (
    <div className="text-[clamp(.7rem,1cqw,1rem)] text-font-minimal">
      <span>{label.price} {t("Flights.listing.currencyEGP")} </span>
      <span>. {stopsLabel}. </span>
      <span>{label.duration}</span>
    </div>
  );
};

export const defaultRenderLabel = (label: SorterLabel | null): ReactNode => <DefaultLabel label={label} />;

export function getActiveSorterLabel(
  dataGroups: Map<string, Map<number | string, any[]>>,
  sorter: any,
  value: boolean,
): ReactNode {
  const group = sorter && dataGroups.get(sorter.key);
  if (!sorter || !group) return null;

  const { minLabel, maxLabel } = computeFlightLabels(group, sorter);
  return defaultRenderLabel(value ? maxLabel : minLabel);
}
